import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { createContext } from 'use-context-selector';

import eventTracker from '@services/event-tracking';
import { GroupsUsersManager, UsersManager } from './manager';
import { UPSERT_GROUP_RELATION_WITH_USER } from './mutation';
import { GET_USERS_QUERY, QUERY_GET_GROUPS_BY_USER, QUERY_GET_GROUP_BY_USER } from './queries';
import { Group, IUserContext, UserInfo } from './types';
import { useAuth } from '@src/wrappers/auth-wrapper';

export const UserInfoContext = createContext({} as IUserContext);
export const UsersContext = createContext({} as UsersContextType);

export const UserInfoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    authStatus: { isAuthenticated },
    user,
    loading: authLoading,
  } = useAuth();

  const [userInfo, setUserInfo] = useState<UserInfo>({
    id: '',
    name: 'not loaded',
    email: '',
    trader: null,
    isAdmin: false,
    isSuperAdmin: false,
    group: { id: '', name: '', units: [], type: null as any },
  });

  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const [isUpsertSuccess, setIsUpsertSuccess] = useState(false);
  const [isLoadingUpsert, setIsLoadingUpsert] = useState(false);
  const [groupsByUser, setGroupsByUser] = useState<Group[]>([]);

  const [fetchGroupByUser] = useLazyQuery(QUERY_GET_GROUP_BY_USER, {
    fetchPolicy: 'network-only',
    onCompleted: ({ getGroupByUser }) => {
      const { id, name, type, units } = getGroupByUser.group;
      setUserInfo((prev) => ({
        ...prev,
        group: { id, name, type, units },
      }));
      setIsUserLoaded(true);
    },
    onError: (e) => {
      setIsUserLoaded(false);
      throw `ERROR: ${e}`;
    },
  });

  const [fetchGroupsByUser] = useLazyQuery(QUERY_GET_GROUPS_BY_USER, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const manager = new GroupsUsersManager(data);
      const groups = manager.groupsByUser.map(({ group }) => group);
      const fallbackGroup = groups[0] || userInfo.group;

      setGroupsByUser(groups);
      setUserInfo((prev) => ({
        ...prev,
        groups,
        group: prev.group?.id ? prev.group : fallbackGroup,
      }));
    },
    onError: (e) => {
      setIsUserLoaded(false);
      throw `ERROR: ${e}`;
    },
  });

  const [upsertGroupRelation, { loading: mutationLoading }] = useMutation(UPSERT_GROUP_RELATION_WITH_USER, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => setIsUpsertSuccess(data.upsertGroupsUser),
    onError: (e) => {
      throw `ERROR: ${e}`;
    },
  });

  const getGroupData = (userId: string) => {
    setIsUserLoaded(false);
    const variables = { id: userId };
    fetchGroupsByUser({ variables });
    return fetchGroupByUser({ variables });
  };

  const handleUpsertGroupRelation = async (groupId: string) => {
    setIsLoadingUpsert(true);
    return upsertGroupRelation({
      variables: {
        input: {
          userId: user?.id,
          groupId,
        },
      },
    });
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      setUserInfo({
        id: user.id,
        email: user.email || '',
        name: user.name || '',
        isAdmin: user.isAdmin || false,
        isSuperAdmin: user.isSuperAdmin || false,
        trader: null,
        photoUrl: user.pictureUrl,
        group: { id: '', name: '', units: [], type: null as any },
      });
      getGroupData(user.id);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (userInfo.id) {
      eventTracker.setUserData({
        name: userInfo.name,
        email: userInfo.email,
        group: userInfo.group?.name || '',
        isSuperAdmin: userInfo.isSuperAdmin ? 'YES' : 'NO',
      });
    }
  }, [userInfo]);

  const setUserGroup = (groupId: string) => {
    const group = groupsByUser.find((g) => g.id === groupId);
    if (group) {
      setUserInfo((prev) => ({ ...prev, group }));
    }
  };

  return (
    <UserInfoContext.Provider
      value={{
        user: userInfo,
        isFetchingUser: authLoading || mutationLoading || isLoadingUpsert,
        isUserLoaded,
        upsertGroupRelationWithUserHandler: handleUpsertGroupRelation,
        isUpsertRelationSuccess: isUpsertSuccess,
        groupsByUser,
        setUserGroup,
      }}
    >
      {children}
    </UserInfoContext.Provider>
  );
};

export type UsersContextType = {
  getUsers: () => void;
  users: UserInfo[];
  loading: boolean;
};

export const UsersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [getUsers, { called, loading }] = useLazyQuery(GET_USERS_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const manager = new UsersManager(data);
      setUsers(manager.users);
    },
    onError: () => {
      setUsers([]);
    },
  });

  useEffect(() => {
    if (!called) getUsers();
  }, [called, getUsers]);

  return <UsersContext.Provider value={{ users, getUsers, loading }}>{children}</UsersContext.Provider>;
};
